/**
 * Manuscript Data — the 30 collectible lore manuscripts of Lost in Labyrinth.
 *
 * Organized in five arcs that tell the story of Goddess Threia, the corrupted
 * guardian Vorath, the Order of Threia, the decline of faith, and the nature
 * of Limbo itself.
 *
 * Each entry contains:
 *   - id:    Unique numeric identifier (1-30)
 *   - arc:   Thematic arc number (1-5)
 *   - title: Manuscript title (displayed in the collection menu)
 *   - body:  Full multi-line text (displayed when reading)
 */

/** @type {Array<{id: number, arc: number, title: string, body: string}>} */
export const MANUSCRIPTS = Object.freeze([
  // ---------------------------------------------------------------
  // Arc 1 — The Goddess and the Creation (I-VI)
  // ---------------------------------------------------------------
  {
    id: 1,
    arc: 1,
    title: 'The First Light',
    body:
      'Before the labyrinth, there was only void. Threia, Goddess of Passage, spoke a single word into the darkness, and from it grew the first corridor. She meant it as a bridge -- a gentle path for the departed to cross from the world of the living into the peace of the Beyond.',
  },
  {
    id: 2,
    arc: 1,
    title: 'The Shape of Mercy',
    body:
      'Threia wove the labyrinth from compassion. Its walls were low, its paths were wide, and a warm light guided every soul toward the exit. None wandered for long. The maze was not a prison but a threshold, and crossing it was the last kindness the dead would know.',
  },
  {
    id: 3,
    arc: 1,
    title: 'The Guardian Appointed',
    body:
      'To tend the labyrinth, Threia chose Vorath from among the celestial servants. He was diligent and silent, a being of shadow and duty. She gave him dominion over every wall and corridor, trusting him to keep the paths clear and the lost souls safe.',
  },
  {
    id: 4,
    arc: 1,
    title: 'The Trace of Souls',
    body:
      'Each soul that passed through the labyrinth left behind a faint residue -- a whisper of what it had been. These traces settled into the walls like dust. Vorath noticed them first. He found that by gathering these traces, he could feel what the souls had felt. Joy. Sorrow. Fear. He found it intoxicating.',
  },
  {
    id: 5,
    arc: 1,
    title: 'The Covenant of Passage',
    body:
      "Threia's law was absolute: no soul shall linger in the labyrinth. Passage is a right, not a privilege. The maze exists to guide, never to hold. This covenant was carved into the first stone of Limbo, where Vorath himself once knelt in devotion.",
  },
  {
    id: 6,
    arc: 1,
    title: 'The Forgotten Tongue',
    body:
      'The prayers to Threia were once spoken in every temple across the land. Now, only fragments remain -- scattered syllables that the faithful press together with trembling hands, hoping the Goddess still listens. The prayer of the H key is the last of these rites.',
  },

  // ---------------------------------------------------------------
  // Arc 2 — Vorath's Corruption (VII-XIII)
  // ---------------------------------------------------------------
  {
    id: 7,
    arc: 2,
    title: 'The First Delay',
    body:
      'It began with small things. A corridor that bent the wrong way. A path that looped back upon itself. Souls that should have crossed in moments began to wander for hours. Vorath told himself it was necessary -- that the maze needed complexity to function. But he knew the truth. He wanted them to stay.',
  },
  {
    id: 8,
    arc: 2,
    title: 'The Hunger',
    body:
      'Vorath discovered that a soul held in the labyrinth too long would fracture, shedding traces far richer than those left in passing. He began to build dead ends. He sealed exits and carved new walls overnight. The labyrinth grew, and so did his appetite.',
  },
  {
    id: 9,
    arc: 2,
    title: 'The Shifting Walls',
    body:
      'The labyrinth was never meant to change. Threia designed it as a fixed, gentle path. It was Vorath who taught the walls to move, who made the corridors rearrange themselves with each new soul. He called it improvement. It was a cage that no soul could learn to escape.',
  },
  {
    id: 10,
    arc: 2,
    title: 'The Drowning Corridors',
    body:
      "Water seeped into the labyrinth first -- not from any natural source but from Vorath's will. He found that tired, frightened souls shed more traces. The flooded corridors slowed them, weakened them, and made them desperate. Their desperation fed him.",
  },
  {
    id: 11,
    arc: 2,
    title: 'The Fires Below',
    body:
      'When water was no longer enough, Vorath reached deeper. He cracked the floor of Limbo and let the fires of the abyss bleed through. Lava pooled in the corridors, scorching the souls that wandered too close. Their pain was a feast he could not resist.',
  },
  {
    id: 12,
    arc: 2,
    title: 'The Poison Garden',
    body:
      'Vorath grew bolder. He cultivated a venom that clung to the maze floor like dew. Souls who touched it were slowed, their essence draining away in a haze of suffering. He planted these patches like a gardener tends his flowers, admiring his own cruelty.',
  },
  {
    id: 13,
    arc: 2,
    title: 'The Iron Teeth',
    body:
      'The spikes were his final addition to the maze. Sharp, merciless, hidden in shadow. Vorath had abandoned all pretense of duty. The labyrinth was no longer a passage. It was a harvest, and he was the reaper.',
  },

  // ---------------------------------------------------------------
  // Arc 3 — The Order of Threia (XIV-XX)
  // ---------------------------------------------------------------
  {
    id: 14,
    arc: 3,
    title: 'The Founding of the Order',
    body:
      "When the first prayers went unanswered, when the dead no longer reached the Beyond, a group of monks gathered in the last temple of Threia. They swore an oath: to enter the labyrinth, to confront what had gone wrong, and to restore the Goddess's covenant. They called themselves the Order of Threia.",
  },
  {
    id: 15,
    arc: 3,
    title: 'The Agmuth Mineral',
    body:
      'Deep beneath the oldest temple, the monks found a vein of shimmering stone that existed in two worlds at once. They called it Agmuth -- the word means "between" in the old tongue. When held, it allowed the bearer to pass through solid matter, as if the walls of reality were merely suggestions.',
  },
  {
    id: 16,
    arc: 3,
    title: 'The Four Relics',
    body:
      'From the purest Agmuth, the Order forged four relics of immense power. Each was a crystallized fragment of the space between worlds. Together, they could bind anything -- even a celestial being -- to a single point in the labyrinth, trapping it as surely as Vorath had trapped the souls.',
  },
  {
    id: 17,
    arc: 3,
    title: 'The Descent',
    body:
      "Twelve monks entered the labyrinth. The corridors shifted around them, Vorath's will pressing against their every step. They used the Agmuth to phase through walls he raised to block their path. Three were lost to the water. Two fell to the fire. But seven reached the heart of Limbo.",
  },
  {
    id: 18,
    arc: 3,
    title: 'The Binding of Vorath',
    body:
      'At the center of the labyrinth, they found him -- vast, formless, bloated with the energy of a thousand trapped souls. The monks placed the four Agmuth relics at the cardinal points and spoke the binding prayer. Vorath screamed. The walls shook. But the seal held, and the Guardian was imprisoned within his own maze.',
  },
  {
    id: 19,
    arc: 3,
    title: 'The Cost',
    body:
      'Of the seven who reached Vorath, only two walked out of the labyrinth. The binding consumed the others -- their souls became part of the seal itself, forever holding the Guardian in place. The Order recorded their names, but time has worn the ink away. Only the word "sacrifice" remains legible.',
  },
  {
    id: 20,
    arc: 3,
    title: 'The Scattering',
    body:
      'With Vorath sealed, the Order believed its work was done. The monks returned to the surface, but the world had changed. Temples had been abandoned. The name of Threia was spoken only in whispers. One by one, the monks grew old and died, and no new acolytes came to replace them.',
  },

  // ---------------------------------------------------------------
  // Arc 4 — The Decline and the Forgotten (XXI-XXV)
  // ---------------------------------------------------------------
  {
    id: 21,
    arc: 4,
    title: 'The Fading Prayer',
    body:
      "Prayers are living things. They need voices to sustain them. As the faithful dwindled, the old rites lost their power. The binding weakened. Vorath, patient in his prison, felt the cracks forming and began to push against the seal once more.",
  },
  {
    id: 22,
    arc: 4,
    title: 'The Labyrinth Remembers',
    body:
      "Even sealed, Vorath's influence seeps through the maze. The walls still shift. The hazards still grow. The bats that screech in the darkness are fragments of his will -- small, cruel extensions of his hunger, drawn to the warmth of any soul that dares to collect what the labyrinth offers.",
  },
  {
    id: 23,
    arc: 4,
    title: 'The Lost Totems',
    body:
      "The monks left Threia Totems scattered through the labyrinth as anchors for the faithful. Each totem is a small vessel of the Goddess's grace. To hold one is to carry a fragment of her protection -- against the bats, against despair, against the creeping darkness of Vorath's will.",
  },
  {
    id: 24,
    arc: 4,
    title: 'The Compass of the Blind Monk',
    body:
      'Brother Aldren lost his sight in the descent but could feel the exit pulling at his soul. Before he died, he crafted a compass that points not north but toward passage -- toward the way out. A few of these compasses still appear in the labyrinth, waiting for a soul worthy of their guidance.',
  },
  {
    id: 25,
    arc: 4,
    title: "Threia's Last Whisper",
    body:
      'It is said that Threia still watches, though she can no longer intervene directly. When a soul prays with enough devotion -- pressing the old syllables together with urgency and faith -- she may answer. A flash of light. A restored heart. A blessing from a Goddess the world has forgotten but who has not forgotten the world.',
  },

  // ---------------------------------------------------------------
  // Arc 5 — The Nature of Limbo (XXVI-XXX)
  // ---------------------------------------------------------------
  {
    id: 26,
    arc: 5,
    title: 'Why the Maze Changes',
    body:
      "The labyrinth was never alive until Vorath made it so. Now it breathes. It rearranges itself with each soul that enters, not by design but by Vorath's restless dreaming. Even imprisoned, his mind wanders the corridors, rebuilding, reshaping, adding new horrors. No two journeys through Limbo are the same.",
  },
  {
    id: 27,
    arc: 5,
    title: 'The Food of the Dead',
    body:
      'Souls in the labyrinth should need nothing. They are beyond hunger, beyond thirst. But Vorath twisted even this. He made the trapped souls feel the drain of life, a phantom hunger that weakens them step by step. The food that appears in the corridors is a memory -- an echo of the living world that briefly restores what Vorath takes.',
  },
  {
    id: 28,
    arc: 5,
    title: 'The Currency of Limbo',
    body:
      "The coins that glitter in the maze are crystallized moments of life -- seconds, minutes, hours that souls shed as they wander. Vorath hoards them, but some slip through the cracks in his attention. To a trapped soul, gathering enough of these fragments can mean the difference between oblivion and one more chance.",
  },
  {
    id: 29,
    arc: 5,
    title: 'The Chests of the Fallen',
    body:
      "The treasure chests that appear in the labyrinth were once the monks' supply caches, left during their descent. Inside, they stored Agmuth fragments, coins, and blessings. The gold chests, sealed with the strongest locks, still sometimes contain Agmuth relics -- the same divine mineral that once bound Vorath himself.",
  },
  {
    id: 30,
    arc: 5,
    title: 'The Soul That Escapes',
    body:
      'There is a prophecy, barely legible, scratched into the oldest manuscript of the Order: "When the labyrinth can no longer hold a single soul, the seal will either shatter or become eternal." No one knows what this means. But every soul that finds the exit, that escapes level after level, weakens Vorath\'s grip -- just a little. Perhaps that is enough.',
  },
]);

/** Total number of manuscripts available in the game. */
export const MANUSCRIPT_COUNT = MANUSCRIPTS.length;

/**
 * Arc display names for the ManuscriptsScene UI.
 * @type {Object<number, string>}
 */
export const ARC_NAMES = Object.freeze({
  1: 'The Goddess and the Creation',
  2: "Vorath's Corruption",
  3: 'The Order of Threia',
  4: 'The Decline and the Forgotten',
  5: 'The Nature of Limbo',
});
